import { safe, state } from './config'
import { isActive } from './journey'
import { cleanStr, maskText, scrubPath } from './scrub'
import { isPending, record } from './transport'

const MAX_FRAMES = 5
const CHROME_FRAME = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/
const FIREFOX_FRAME = /^\s*(.*?)@(.+?):(\d+):(\d+)\s*$/
const ASSET_URL = /(?:https?:\/\/[^\s'"()]+|\/[^\s'"()]+\.(?:js|mjs|css))/
// Vite names chunks name-<8 character hash>.js and the hash changes with every build.
const BUNDLE_HASH = /-[A-Za-z0-9_-]{8}(?=\.(?:m?js|css)\b)/g
const LINE_COLUMN = /(?::\d+)+$/

let seen = new Map()

function basename(url) {
  return String(url).split(/[?#]/)[0].split('/').pop() || null
}

// Fingerprints leave out the bundle hash and line:column so a rebuild does not turn a known error into a new one.
function stableSource(source) {
  return source ? source.replace(LINE_COLUMN, '').replace(BUNDLE_HASH, '') : ''
}

function parseFrame(line) {
  const match = line.match(CHROME_FRAME) ?? line.match(FIREFOX_FRAME)

  if (!match) {
    return null
  }

  const [, fn, url, lineNumber, column] = match
  const location = `${basename(url)}:${lineNumber}:${column}`

  return { location, text: fn ? `${fn.trim()} ${location}` : location }
}

function stackFrames(stack) {
  if (typeof stack !== 'string') {
    return []
  }

  return stack.split('\n').map(parseFrame).filter(Boolean).slice(0, MAX_FRAMES)
}

// 64-bit FNV-1a style hash as 16 hex characters; only needs to be stable, not secure.
function hash16(input) {
  let first = 0x811c9dc5
  let second = 0x9747b28c

  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index)

    first = Math.imul(first ^ code, 0x01000193)
    second = Math.imul(second ^ code, 0x5bd1e995)
  }

  return (first >>> 0).toString(16).padStart(8, '0') + (second >>> 0).toString(16).padStart(8, '0')
}

function describe(error) {
  if (error instanceof Error || (error && typeof error === 'object' && 'message' in error)) {
    return { name: error.name || 'Error', message: String(error.message ?? ''), stack: error.stack }
  }

  return { name: 'Error', message: typeof error === 'string' ? error : '', stack: null }
}

function vueInfo(info) {
  if (typeof info !== 'string') {
    return null
  }

  // Production Vue passes a link to its error reference instead of the description.
  return info.includes('runtime-') ? `runtime-${info.split('runtime-')[1]}` : info
}

export function resetErrors() {
  seen = new Map()
}

export function recordError(error, handler, { name, source, info } = {}) {
  // Before the first navigate the transport holds events until it knows whether capture is on.
  if (state.booted && !isActive()) {
    return
  }

  const details = describe(error)
  const frames = stackFrames(details.stack)
  const errorName = name ?? details.name
  const errorSource = source ?? frames[0]?.location ?? null
  const message = details.message.slice(0, 100).replace(BUNDLE_HASH, '')
  const fingerprint = hash16(`${errorName}|${message}|${stableSource(errorSource)}`)
  const previous = seen.get(fingerprint)

  // Repeats on one page view bump the count of the unsent event instead of adding rows.
  if (previous && isPending(previous)) {
    previous.props.count = Math.min((previous.props.count ?? 1) + 1, 1000)

    return
  }

  const event = record('error.js', {
    target: cleanStr(errorName, 64),
    props: {
      message: maskText(details.message, 300),
      source: cleanStr(errorSource, 128),
      fingerprint,
      handler,
      frames: frames.map((frame) => cleanStr(frame.text, 96)).filter(Boolean),
      vue_info: cleanStr(vueInfo(info), 64),
      count: 1,
    },
  })

  if (event) {
    seen.set(fingerprint, event)
  }
}

function onWindowError(event) {
  // Opaque cross-origin "Script error." and benign ResizeObserver loop notices carry nothing to act on.
  if (!event.error && /^(?:Script error\.?|ResizeObserver loop\b.*)$/.test(event.message ?? '')) {
    return
  }

  const source = event.filename ? `${basename(event.filename)}:${event.lineno ?? 0}:${event.colno ?? 0}` : undefined

  recordError(event.error ?? event.message, 'window', { source })
}

function onRejection(event) {
  const reason = event.reason

  recordError(reason, 'promise', reason === undefined || reason === null ? { name: 'UnhandledRejection' } : {})
}

function onPreloadError(event) {
  if (!isActive()) {
    return
  }

  const message = String(event.payload?.message ?? '')
  const url = message.match(ASSET_URL)?.[0]

  record('error.chunk', { props: { path: url ? scrubPath(url) : null } })
}

export function installErrors() {
  window.addEventListener('error', safe(onWindowError))
  window.addEventListener('unhandledrejection', safe(onRejection))
  window.addEventListener('vite:preloadError', safe(onPreloadError))
}
